/**
 * Subscriptions Pinia Store
 * 
 * MIGRATION FROM VUEX:
 * This store replaces the Vuex subscriptions module (resources/assets/js/vuex/stores/subscriptions.js)
 * 
 * Key differences:
 * - No mutations: actions directly modify state using ref()
 * - No getters pattern: state refs are directly reactive
 * - Composition API style using defineStore()
 * - Manages its own localStorage persistence (separate from Vuex)
 * 
 * STORAGE:
 * - Persisted to localStorage under key: "${userEmail}@pinia"
 * - Structure: { subscriptionsStore: { allSubscriptions, currentSubscription } }
 * - This keeps Pinia data separate from Vuex data to avoid quota issues
 * 
 * USAGE IN MODELS:
 * The Subscription model (extending AbstractPiniaModel) accesses this store:
 * - static piniaStore() { return useSubscriptionsStore() }
 * - Subscription.getAll() → store.allSubscriptions
 * - Subscription.setAll(data) → store.setAllSubscriptions(data)
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { getCurrentStorageUser } from '../../common/localStorage/currentStorageUser'

export const useSubscriptionsStore = defineStore('subscriptions', () => {
    // ========== STATE ==========
    // Using ref() for reactive state (Composition API pattern)
    
    /**
     * Array of all subscription objects
     * Structure: { id, profession_id, start_datum, end_datum, ... }
     */
    const allSubscriptions = ref([])
    
    /**
     * Currently selected subscription
     * Used to track which subscription the user is working with
     */
    const currentSubscription = ref(undefined)

    // ========== GETTERS (Computed) ==========
    // In Pinia, getters are simply computed properties (no special pattern needed)
    
    /**
     * Computed getter for all subscriptions
     * Returns the reactive array directly
     */
    const getAllSubscriptions = computed(() => allSubscriptions.value)
    
    /**
     * Computed getter for current subscription
     * Returns the current subscription or undefined
     */
    const getCurrentSubscription = computed(() => currentSubscription.value)
    
    /**
     * Factory getter: returns a function that finds subscription by ID
     * Usage: store.getSubscriptionById(5)
     */
    const getSubscriptionById = computed(() => (id) =>
        allSubscriptions.value.find((s) => s.id == id)
    )

    // ========== ACTIONS ==========
    // In Pinia, actions are just functions that modify state
    // No mutation/action separation like Vuex
    
    /**
     * Replace all subscriptions with new data
     * Syncs with localStorage after update
     * @param {Array} subscriptions - New subscriptions array
     */
    const setAllSubscriptions = (subscriptions) => {
        allSubscriptions.value = subscriptions
        persistToLocalStorage()
    }

    /**
     * Add new subscriptions to existing list
     * Syncs with localStorage after update
     * @param {Array} subscriptions - Subscriptions to add
     */
    const addToAllSubscriptions = (subscriptions) => {
        subscriptions.forEach((newSub) => allSubscriptions.value.push(newSub))
        persistToLocalStorage()
    }

    /**
     * Set the current subscription
     * Syncs with localStorage after update
     * @param {Object} subscription - Subscription object to set as current
     */
    const setCurrentSubscription = (subscription) => {
        currentSubscription.value = subscription
        persistToLocalStorage()
    }

    /**
     * Find and set current subscription by its ID
     * Syncs with localStorage after update
     * @param {number} id - Subscription ID to find and set
     */
    const setCurrentSubscriptionById = (id) => {
        currentSubscription.value = allSubscriptions.value.find((s) => s.id == id)
        persistToLocalStorage()
    }

    /**
     * Find and set current subscription by profession ID
     * Syncs with localStorage after update
     * @param {number} professionId - Profession ID to find subscription by
     */
    const setCurrentSubscriptionByProfessionID = (professionId) => {
        currentSubscription.value = allSubscriptions.value.find(
            (s) => s.profession_id == professionId
        )
        persistToLocalStorage()
    }

    // ========== LOCAL STORAGE PERSISTENCE ==========
    // Unlike Vuex (which uses VuexPersistence plugin), 
    // Pinia stores must manage their own persistence
    
    /**
     * Generate the localStorage key for this store
     * Format: "${userEmail}@pinia"
     * This keeps Pinia data separate from Vuex to avoid localStorage quota issues
     * @returns {string} Storage key
     */
    const getPiniaStorageKey = () => {
        return getCurrentStorageUser() + '@pinia'
    }

    /**
     * Save store state to localStorage
     * Called after every state mutation
     * Uses nested structure: { subscriptionsStore: { allSubscriptions, currentSubscription } }
     * This allows multiple Pinia modules to share the same localStorage key
     * @throws QuotaExceededError if localStorage is full
     */
    const persistToLocalStorage = () => {
        const key = getPiniaStorageKey()
        try {
            // Get existing Pinia localStorage data or create new
            let data = {}
            const stored = localStorage.getItem(key)
            if (stored) {
                try {
                    data = JSON.parse(stored)
                } catch (e) {
                    console.warn('Failed to parse existing Pinia localStorage, creating new:', e)
                    data = {}
                }
            }
            
            // Save subscriptions data but preserve other stores (like userSettingsStore)
            data.subscriptionsStore = {
                allSubscriptions: allSubscriptions.value,
                currentSubscription: currentSubscription.value,
            }
            
            localStorage.setItem(key, JSON.stringify(data))
        } catch (e) {
            if (e.name === 'QuotaExceededError' || e.message.includes('quota')) {
                console.warn('🛒 QuotaExceededError detected in subscriptions store')
                throw e
            }
        }
    }

    /**
     * Load store state from localStorage on initialization
     * Called once when the store is created
     * Safely handles missing/corrupt data by checking existence before accessing
     */
    const loadFromLocalStorage = () => {
        const key = getPiniaStorageKey()
        try {
            const stored = localStorage.getItem(key)
            if (stored) {
                const data = JSON.parse(stored)
                if (data.subscriptionsStore) {
                    const subscriptionsData = data.subscriptionsStore
                    if (subscriptionsData.allSubscriptions) allSubscriptions.value = subscriptionsData.allSubscriptions
                    if (subscriptionsData.currentSubscription) currentSubscription.value = subscriptionsData.currentSubscription
                }
            }
        } catch (e) {
            console.warn('Failed to load subscriptions from localStorage:', e)
        }
    }

    // ========== INITIALIZATION ==========
    // Load from localStorage when store is first created
    loadFromLocalStorage()

    // ========== STORE INTERFACE ==========
    // Return all state, getters, and actions
    // These are what external code (models, components) will access
    return {
        // State
        allSubscriptions,
        currentSubscription,
        // Getters (Computed)
        getAllSubscriptions,
        getCurrentSubscription,
        getSubscriptionById,
        // Actions
        setAllSubscriptions,
        addToAllSubscriptions,
        setCurrentSubscription,
        setCurrentSubscriptionById,
        setCurrentSubscriptionByProfessionID,
    }
})

